import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { TransactionalOrderUI } from '@/api/TransactionalOrdersApi';
import { useFunds } from '@/context/FundsContext';
import { useTransactionalOrders } from '@/context/TransactionalOrdersContext';
import { formatCurrency } from '@/utils/formatCurrency';

type LinkTransactionDialogProps = {
  order: TransactionalOrderUI | null;
  open: boolean;
  onClose: () => void;
};

export const LinkTransactionDialog = ({ order, open, onClose }: LinkTransactionDialogProps): React.ReactNode => {
  const { transactions } = useFunds();
  const { linkToTransaction } = useTransactionalOrders();
  const [selectedTransactionId, setSelectedTransactionId] = useState<number | null>(null);
  const [linking, setLinking] = useState(false);

  const sortedTransactions = useMemo(() => {
    if (!transactions) return [];
    return [...transactions].sort((a, b) => {
      const aTime = a.transaction_time ? a.transaction_time * 1000 : new Date(a.date!).getTime();
      const bTime = b.transaction_time ? b.transaction_time * 1000 : new Date(b.date!).getTime();
      return bTime - aTime;
    });
  }, [transactions]);

  const handleLink = async () => {
    if (!order || !selectedTransactionId) return;

    setLinking(true);
    try {
      await linkToTransaction(order.id, selectedTransactionId);
      setSelectedTransactionId(null);
      onClose();
    } catch (err) {
      console.error('Failed to link transaction:', err);
    } finally {
      setLinking(false);
    }
  };

  const handleClose = () => {
    setSelectedTransactionId(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Link to Existing Transaction</DialogTitle>
          {order && (
            <p className="text-sm text-muted-foreground">
              Select a funds transaction to link with broker order #{order.ticket} ({formatCurrency(Math.abs(order.amount))})
            </p>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead></TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedTransactions.map((transaction) => {
                const dateObj = transaction.transaction_time
                  ? new Date(transaction.transaction_time * 1000)
                  : new Date(transaction.date!);
                const day = dateObj.getDate().toString().padStart(2, '0');
                const month = dateObj.toLocaleString('en-US', { month: 'short' });
                const year = dateObj.getFullYear();
                const displayDate = `${day}-${month}-${year}`;

                const transactionType = transaction.transaction_type || transaction.type;
                const isDeposit = transactionType === 'deposit';
                const isSelected = selectedTransactionId === transaction.id;

                return (
                  <TableRow
                    key={transaction.id}
                    className={`cursor-pointer hover:bg-muted/50 ${isSelected ? 'bg-accent' : ''}`}
                    onClick={() => setSelectedTransactionId(transaction.id ?? null)}
                  >
                    <TableCell>
                      <input
                        type="radio"
                        checked={isSelected}
                        onChange={() => setSelectedTransactionId(transaction.id ?? null)}
                        className="h-4 w-4"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{displayDate}</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium capitalize">{transactionType}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className={`font-medium ${isDeposit ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(Math.abs(transaction.amount))}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}

              {sortedTransactions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    No funds transactions available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={linking}>Cancel</Button>
          <Button onClick={handleLink} disabled={linking || !selectedTransactionId}>
            {linking ? 'Linking...' : 'Link Transaction'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
